import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RenderOutputPolicyService, RenderOutputSettings } from '../../../../core/render-runtime';
import { NarrationTtsService } from '../../../../core/services/narration-tts.service';
import { JivuCommand, JivuCommandHandoffPayload } from '../../../JivuCommand/services/jivu-command.models';
import { JivuCommandProgressReporter } from '../../../JivuCommand/services/jivu-command-progress-reporter.service';
import { ReviewService, SmartVideoAsset, SmartVideoAssetGenerateResponse, SmartVideoAssetRenderItem } from '../../../Review/services/review.service';
import { AssetSequenceCommandRenderService } from './asset-sequence-command-render.service';

@Injectable({ providedIn: 'root' })
export class AssetSequenceCommandRunnerService {
  private readonly reviewService = inject(ReviewService);
  private readonly commandRender = inject(AssetSequenceCommandRenderService);
  private readonly progressReporter = inject(JivuCommandProgressReporter);
  private readonly renderOutputPolicy = inject(RenderOutputPolicyService);
  private readonly narrationTts = inject(NarrationTtsService);
  private readonly inFlight = new Set<string>();

  async drive(command: JivuCommand, payload: JivuCommandHandoffPayload): Promise<void> {
    if (this.inFlight.has(command.id)) return;
    this.inFlight.add(command.id);

    const bakedNarrationUrls: Array<string | null> = [];
    try {
      const metadata = this.objectValue(payload.metadata);
      const selected = this.orderedAssets(metadata);
      if (!selected.length) {
        throw new Error('Jivu Command assets payload is missing selected assets.');
      }

      const aspectRatio = this.aspectRatio(metadata['aspectRatio']);
      const title = this.stringValue(metadata['title']) || command.title || 'assets-library video';
      const descriptionMode = this.stringValue(metadata['descriptionMode']) === 'existing' ? 'existing' : 'change';
      const durations = this.objectValue(metadata['assetDurations']);
      const runtimeDescriptions = this.objectValue(metadata['runtimeDescriptions']);

      await this.progressReporter.reportNow(command.id, 10, 'Preparing assets-library command.');
      const response = await firstValueFrom(this.reviewService.generateSmartVideoFromAssets({
        nichePresetId: this.stringValue(metadata['nichePresetId']),
        customNicheName: this.stringValue(metadata['customNicheName']),
        title,
        descriptionMode,
        aspectRatio,
        autoReview: false,
        assets: selected.map(asset => ({
          assetId: asset.id,
          fileId: asset.fileId,
          kind: asset.kind,
          description: this.assetDescriptionText(asset, runtimeDescriptions),
          durationSeconds: this.assetDuration(asset, durations, runtimeDescriptions),
          dateKey: asset.dateKey,
          topic: asset.topic
        }))
      }));

      await this.progressReporter.reportNow(command.id, 20, 'Resolving assets-library media.');
      const renderAssets = await this.resolveAssetRenderItems(response.renderAssets, selected, metadata, durations, runtimeDescriptions);
      const responseDescription = this.cleanText(response.description) || response.description;
      const voiceEnabled = metadata['voiceEnabled'] !== false;

      if (voiceEnabled) {
        await this.bakeNarration(command.id, renderAssets, this.stringValue(metadata['selectedVoiceKey']), bakedNarrationUrls);
      } else {
        for (const asset of renderAssets) {
          asset.narrationUrl = null;
          asset.narrationPlaybackRate = null;
          asset.durationSeconds = this.isVideoAsset(asset)
            ? this.fixedVideoAssetDuration(asset)
            : this.manualImageAssetDuration(asset.durationSeconds);
        }
      }

      const narrationSegments = this.assetNarrationSegments(renderAssets);
      const narrationWasBaked = voiceEnabled && bakedNarrationUrls.some(url => url != null);
      const renderOutput = this.renderOutput(metadata['renderOutput'], aspectRatio, renderAssets);

      if (response.mode !== 'client-render') {
        throw new Error('assets-library command returned a queued backend render instead of a client render payload.');
      }

      await this.commandRender.drive(command.id, {
        renderOptions: {
          title: response.title,
          description: responseDescription,
          assets: renderAssets,
          aspectRatio,
          includeSubtitles: metadata['subtitlesEnabled'] === true,
          outputSettings: renderOutput
        },
        reservePayload: {
          title: response.title,
          description: responseDescription,
          isShort: aspectRatio === '9:16',
          pipeline: 'SmartVideo'
        },
        savePayloadBase: {
          title: response.title,
          description: responseDescription,
          runtimeNarrationEnabled: narrationWasBaked ? false : voiceEnabled,
          runtimeNarrationVoiceName: null,
          runtimeNarrationVoiceUri: null,
          runtimeNarrationVoiceLang: null,
          captionTranscript: this.assetNarrationTranscript(narrationSegments),
          captionSegments: narrationSegments
        },
        completeResult: {
          commandType: 'asset_sequence_render',
          assetIds: selected.map(asset => asset.id),
          mode: response.mode,
          jobId: response.jobId || null
        },
        completeMessage: 'assets-library command completed on target device.'
      });
    } finally {
      for (const url of bakedNarrationUrls) {
        if (url) {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        }
      }
      this.inFlight.delete(command.id);
    }
  }

  private orderedAssets(metadata: Record<string, unknown>): SmartVideoAsset[] {
    const assets = Array.isArray(metadata['assets']) ? metadata['assets'] as SmartVideoAsset[] : [];
    const order = Array.isArray(metadata['selectedOrder'])
      ? (metadata['selectedOrder'] as unknown[]).map(id => String(id || '').trim()).filter(Boolean)
      : [];
    if (!order.length) return assets.filter(asset => !!asset?.id);

    const byId = new Map(assets.filter(asset => !!asset?.id).map(asset => [asset.id, asset]));
    return order.map(id => byId.get(id)).filter((asset): asset is SmartVideoAsset => !!asset);
  }

  private async resolveAssetRenderItems(
    responseAssets: SmartVideoAssetGenerateResponse['renderAssets'],
    selected: SmartVideoAsset[],
    metadata: Record<string, unknown>,
    durations: Record<string, unknown>,
    runtimeDescriptions: Record<string, unknown>
  ): Promise<SmartVideoAssetRenderItem[]> {
    const manifest = responseAssets || [];
    if (!manifest.length) {
      throw new Error('assets-library render manifest is missing.');
    }
    if (manifest.length !== selected.length) {
      throw new Error('assets-library render manifest does not match the selected assets.');
    }

    const nichePresetId = this.stringValue(metadata['nichePresetId']);
    const resolved: SmartVideoAssetRenderItem[] = [];
    for (let index = 0; index < selected.length; index += 1) {
      const selectedAsset = selected[index];
      const manifestAsset = manifest[index];
      this.assertAssetManifestMatch(selectedAsset, manifestAsset, index);
      const directUrl = await firstValueFrom(this.reviewService.getSmartVideoAssetSignedUrl(selectedAsset.id, {
        nichePresetId,
        dateKey: selectedAsset.dateKey,
        topic: selectedAsset.topic
      }));
      resolved.push({
        ...manifestAsset,
        assetId: selectedAsset.id,
        fileId: selectedAsset.fileId || manifestAsset.fileId,
        kind: manifestAsset.kind || selectedAsset.kind,
        name: manifestAsset.name || selectedAsset.name,
        directUrl,
        description: this.selectedAssetRenderDescription(selectedAsset, manifestAsset, runtimeDescriptions),
        durationSeconds: this.selectedAssetRenderDuration(selectedAsset, manifestAsset, durations, runtimeDescriptions)
      });
    }

    return resolved;
  }

  private async bakeNarration(
    commandId: string,
    assets: SmartVideoAssetRenderItem[],
    selectedVoiceKey: string | null,
    bakedNarrationUrls: Array<string | null>
  ): Promise<void> {
    if (!this.narrationTts.isSupported()) {
      throw new Error('Piper voice is still loading. Turn Voice off for manual image timing, or try again in a moment.');
    }

    const voiceId = this.piperVoiceId(selectedVoiceKey);
    for (let index = 0; index < assets.length; index += 1) {
      const asset = assets[index];
      const rawText = this.cleanText(asset.description || asset.name);
      await this.progressReporter.reportNow(commandId, Math.round(22 + (index / Math.max(1, assets.length)) * 12), `Generating narration ${index + 1}/${assets.length}...`);
      if (!rawText) {
        throw new Error(`Scene ${index + 1} needs narration text before Voice rendering.`);
      }

      const videoScene = this.isVideoAsset(asset);
      const fixedSceneDuration = videoScene ? this.fixedVideoAssetDuration(asset) : 0;
      let narrationText = videoScene ? this.fitVideoNarrationText(rawText, fixedSceneDuration) : rawText;
      let ttsResult = await this.narrationTts.synthesize(narrationText, voiceId);
      let ttsDuration = this.ttsSceneDurationSeconds(ttsResult.durationSeconds, narrationText);
      if (videoScene && ttsDuration > fixedSceneDuration * 1.25) {
        const allowedDuration = Math.max(1, fixedSceneDuration * 1.18);
        const nextWordLimit = Math.max(4, Math.floor(this.wordCount(narrationText) * (allowedDuration / ttsDuration)));
        const shorterText = this.trimNarrationTextToWords(narrationText, nextWordLimit);
        if (shorterText && shorterText !== narrationText) {
          try { URL.revokeObjectURL(ttsResult.objectUrl); } catch { /* ignore */ }
          narrationText = shorterText;
          ttsResult = await this.narrationTts.synthesize(narrationText, voiceId);
          ttsDuration = this.ttsSceneDurationSeconds(ttsResult.durationSeconds, narrationText);
        }
      }

      const sceneDuration = videoScene ? fixedSceneDuration : ttsDuration;
      asset.description = narrationText;
      asset.durationSeconds = sceneDuration;
      asset.narrationPlaybackRate = videoScene ? this.fitNarrationPlaybackRate(ttsDuration, sceneDuration) : 1;
      asset.narrationUrl = ttsResult.objectUrl;
      bakedNarrationUrls.push(ttsResult.objectUrl);
    }
  }

  private assertAssetManifestMatch(selectedAsset: SmartVideoAsset, manifestAsset: SmartVideoAssetRenderItem, index: number): void {
    const selectedAssetId = selectedAsset.id?.trim();
    const selectedFileId = selectedAsset.fileId?.trim();
    const manifestAssetId = manifestAsset.assetId?.trim();
    const manifestFileId = manifestAsset.fileId?.trim();
    if (!selectedAssetId) {
      throw new Error(`Selected assets-library item ${index + 1} is missing an asset id.`);
    }
    if (!manifestAssetId && !manifestFileId) {
      throw new Error(`assets-library manifest item ${index + 1} is missing an asset id.`);
    }
    if (manifestAssetId && manifestAssetId !== selectedAssetId) {
      throw new Error('assets-library manifest asset id mismatch.');
    }
    if (manifestFileId && manifestFileId !== selectedAssetId && manifestFileId !== selectedFileId) {
      throw new Error('assets-library manifest file id mismatch.');
    }
  }

  private selectedAssetRenderDescription(
    selectedAsset: SmartVideoAsset,
    manifestAsset: SmartVideoAssetRenderItem,
    runtimeDescriptions: Record<string, unknown>
  ): string {
    const selectedDescription = this.cleanText(this.assetDescriptionText(selectedAsset, runtimeDescriptions));
    const manifestDescription = this.cleanText(manifestAsset.description);
    return manifestDescription || selectedDescription || manifestAsset.description || selectedAsset.description || selectedAsset.topic || selectedAsset.name;
  }

  private selectedAssetRenderDuration(
    selectedAsset: SmartVideoAsset,
    manifestAsset: SmartVideoAssetRenderItem,
    durations: Record<string, unknown>,
    runtimeDescriptions: Record<string, unknown>
  ): number {
    const selectedDuration = Number(this.assetDuration(selectedAsset, durations, runtimeDescriptions));
    if (Number.isFinite(selectedDuration) && selectedDuration > 0) {
      return Math.round(selectedDuration * 10) / 10;
    }

    const manifestDuration = Number(manifestAsset.durationSeconds);
    if (Number.isFinite(manifestDuration) && manifestDuration > 0) {
      return Math.round(manifestDuration * 10) / 10;
    }

    return this.defaultAssetDuration(selectedAsset);
  }

  private assetDuration(
    asset: Pick<SmartVideoAsset, 'id' | 'kind' | 'durationSeconds'> & Partial<Pick<SmartVideoAsset, 'description' | 'topic' | 'name'>>,
    durations: Record<string, unknown>,
    runtimeDescriptions: Record<string, unknown>
  ): number {
    const override = Number(durations[asset.id]);
    if (Number.isFinite(override) && override > 0) {
      return Math.round(override * 10) / 10;
    }
    if (this.isVideoAsset(asset)) {
      return this.fixedVideoAssetDuration(asset);
    }
    return this.manualImageAssetDuration(asset.durationSeconds)
      || this.estimatedSpeechDurationSeconds(this.assetDescriptionText(asset, runtimeDescriptions));
  }

  private defaultAssetDuration(asset: SmartVideoAsset): number {
    if (this.isVideoAsset(asset)) {
      const duration = Number(asset.durationSeconds);
      return Number.isFinite(duration) && duration > 0 ? Math.round(duration * 10) / 10 : 5;
    }

    return 10;
  }

  private renderOutput(existing: unknown, aspectRatio: '9:16' | '16:9', assets: SmartVideoAssetRenderItem[]): RenderOutputSettings {
    const durationSeconds = assets.reduce((sum, asset) => sum + this.normalizedAssetRenderDuration(asset.durationSeconds), 0) || 120;
    return this.renderOutputPolicy.resolve({
      surface: 'review-image-sequence',
      durationSeconds,
      aspectRatio,
      width: aspectRatio === '16:9' ? 1920 : 1080,
      height: aspectRatio === '16:9' ? 1080 : 1920,
      contentKind: 'generated-image-sequence',
      existing
    });
  }

  private assetNarrationSegments(assets: SmartVideoAssetGenerateResponse['renderAssets']): Array<{ start: number; end: number; text: string }> {
    let cursor = 0;
    return (assets || [])
      .map(asset => {
        const duration = this.normalizedAssetRenderDuration(asset.durationSeconds);
        const text = this.cleanText(asset.description || asset.name);
        const segment = {
          start: Math.round(cursor * 10) / 10,
          end: Math.round((cursor + duration) * 10) / 10,
          text
        };
        cursor += duration;
        return segment;
      })
      .filter(segment => segment.text && segment.end > segment.start);
  }

  private assetNarrationTranscript(segments: Array<{ text: string }>): string {
    return segments
      .map(segment => segment.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private normalizedAssetRenderDuration(duration: number | null | undefined): number {
    const value = Number(duration);
    return Number.isFinite(value) && value > 0
      ? Math.max(0.1, Math.round(value * 10) / 10)
      : 10;
  }

  private assetDescriptionText(
    asset: (Pick<SmartVideoAsset, 'id'> & Partial<Pick<SmartVideoAsset, 'description' | 'topic' | 'name'>>) | null | undefined,
    runtimeDescriptions: Record<string, unknown>
  ): string {
    if (!asset) return '';
    const runtimeDescription = this.stringValue(runtimeDescriptions[asset.id]);
    const cleaned = this.cleanText(runtimeDescription || asset.description || asset.topic || asset.name);
    return cleaned || 'Show this scene clearly.';
  }

  private cleanText(value: string | null | undefined): string {
    return String(value || '')
      .replace(/\b(?:image|photo|scene)\s+\d+\s*[:.\-]?\s*/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private isVideoAsset(asset: Pick<SmartVideoAsset, 'kind'> | Pick<SmartVideoAssetRenderItem, 'kind'>): boolean {
    return (asset.kind || '').toLowerCase().includes('video');
  }

  private fixedVideoAssetDuration(asset: Pick<SmartVideoAsset, 'durationSeconds'> | Pick<SmartVideoAssetRenderItem, 'durationSeconds'>): number {
    const duration = Number(asset.durationSeconds);
    return Number.isFinite(duration) && duration > 0 ? Math.round(duration * 10) / 10 : 5;
  }

  private manualImageAssetDuration(value: number | null | undefined): number {
    const duration = Number(value);
    return Number.isFinite(duration) && duration > 0 ? Math.round(duration * 10) / 10 : 10;
  }

  private estimatedSpeechDurationSeconds(text: string | null | undefined): number {
    const words = this.wordCount(text || '');
    return Math.max(1, Math.round(((words / 2.5) + 0.2) * 10) / 10);
  }

  private ttsSceneDurationSeconds(durationSeconds: number | null | undefined, text: string): number {
    const estimated = this.estimatedSpeechDurationSeconds(text);
    const duration = Number(durationSeconds);
    if (Number.isFinite(duration) && duration > 0.1) {
      const rounded = Math.round(duration * 10) / 10;
      const lowerBound = Math.max(0.4, estimated * 0.35);
      const upperBound = Math.max(estimated + 6, estimated * 2.5);
      if (rounded >= lowerBound && rounded <= upperBound) {
        return rounded;
      }
    }

    return estimated;
  }

  private fitNarrationPlaybackRate(ttsDurationSeconds: number, sceneDurationSeconds: number): number {
    if (!Number.isFinite(ttsDurationSeconds) || ttsDurationSeconds <= 0) return 1;
    if (!Number.isFinite(sceneDurationSeconds) || sceneDurationSeconds <= 0) return 1;
    return Math.round(Math.max(0.85, Math.min(1.25, ttsDurationSeconds / sceneDurationSeconds)) * 100) / 100;
  }

  private fitVideoNarrationText(text: string, sceneDurationSeconds: number): string {
    const cleaned = this.cleanText(text);
    if (!cleaned) return '';
    const duration = Number(sceneDurationSeconds);
    if (!Number.isFinite(duration) || duration <= 0) return cleaned;
    const maxWords = Math.max(4, Math.floor(duration * 2.5 * 1.25));
    if (this.wordCount(cleaned) <= maxWords) return cleaned;
    return this.trimNarrationTextToWords(cleaned, maxWords);
  }

  private trimNarrationTextToWords(text: string, maxWords: number): string {
    const cleaned = this.cleanText(text);
    const limit = Math.max(1, Math.floor(maxWords));
    if (!cleaned || this.wordCount(cleaned) <= limit) return cleaned;
    return cleaned.split(/\s+/).slice(0, limit).join(' ');
  }

  private wordCount(value: string): number {
    return (value || '').trim().split(/\s+/).filter(Boolean).length;
  }

  private piperVoiceId(selectedVoiceKey: string | null): string {
    return selectedVoiceKey?.startsWith('piper:')
      ? selectedVoiceKey.replace(/^piper:/, '')
      : this.narrationTts.defaultVoiceId;
  }

  private aspectRatio(value: unknown): '9:16' | '16:9' {
    return value === '16:9' ? '16:9' : '9:16';
  }

  private objectValue(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : {};
  }

  private stringValue(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }
}
